import React, { useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  alpha,
  useTheme,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ShieldIcon from "@mui/icons-material/Shield";
import { TranslateContext } from "../../context/TranslateContext";
import { GlobalContext } from "../../context/GlobalContext";
import { useHistory } from "react-router-dom";

const AdminLogin = () => {
  const history = useHistory();
  const { lang } = React.useContext(TranslateContext);
  const { hitAxios } = React.useContext(GlobalContext);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [showPassword, setShowPassword] = useState(false);
  const [state, setState] = React.useState({ email: "", password: "" });

  const handleLogin = async () => {
    const res = await hitAxios({
      path: "/api/admin/login",
      post: true,
      obj: state,
    });
    if (res.data.success) {
      localStorage.setItem(
        process.env.REACT_APP_TOKEN + "_admin",
        res.data.token,
      );
      history.push("/admin");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        background: isDark
          ? "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #0F172A 100%)"
          : "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 50%, #F5F3FF 100%)",
      }}
    >
      <Box
        sx={{
          position: "fixed",
          top: "20%",
          right: "20%",
          width: 350,
          height: 350,
          borderRadius: "50%",
          background: alpha(theme.palette.primary.main, 0.07),
          filter: "blur(80px)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "fixed",
          bottom: "20%",
          left: "15%",
          width: 250,
          height: 250,
          borderRadius: "50%",
          background: alpha(theme.palette.primary.main, 0.05),
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="xs" sx={{ position: "relative", zIndex: 1 }}>
        {/* Security Badge */}
        <Stack direction="row" justifyContent="center" mb={2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 2,
              py: 0.75,
              borderRadius: 10,
              bgcolor: alpha(theme.palette.success.main, 0.1),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            <ShieldIcon sx={{ fontSize: 14, color: "success.main" }} />
            <Typography
              variant="caption"
              color="success.main"
              fontWeight={700}
              sx={{ fontSize: "0.7rem" }}
            >
              Secured Admin Portal
            </Typography>
          </Box>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            bgcolor: isDark
              ? alpha(theme.palette.background.paper, 0.8)
              : "background.paper",
            backdropFilter: "blur(20px)",
            border: `1px solid ${alpha(isDark ? "#ffffff" : "#000000", 0.08)}`,
            boxShadow: isDark
              ? `0 24px 80px ${alpha("#000", 0.5)}`
              : `0 24px 60px ${alpha("#000", 0.1)}`,
          }}
        >
          <Box textAlign="center" mb={3.5}>
            <Box
              sx={{
                width: 68,
                height: 68,
                borderRadius: "50%",
                margin: "0 auto 16px",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.45)}`,
              }}
            >
              <AdminPanelSettingsIcon sx={{ fontSize: 34, color: "#fff" }} />
            </Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ letterSpacing: "-0.02em" }}
            >
              {lang.adminLogin || "Admin Login"}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {lang.adminLoginSubtitle ||
                "Sign in to access the admin dashboard"}
            </Typography>
          </Box>

          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              placeholder={lang.emailPlaceholder || "Admin Email"}
              type="email"
              value={state.email}
              onChange={(e) => setState({ ...state, email: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder={lang.passwordPlaceholder || "Password"}
              type={showPassword ? "text" : "password"}
              value={state.password}
              onChange={(e) => setState({ ...state, password: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      size="small"
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 18 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleLogin}
              sx={{
                py: 1.25,
                fontWeight: 600,
                borderRadius: 2,
                mt: 0.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                "&:hover": {
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.55)}`,
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {lang.signIn || "Sign In"}
            </Button>

            <Box textAlign="center" mt={1}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ cursor: "pointer", "&:hover": { color: "primary.main" } }}
                onClick={() => history.push("/admin/forgot-password")}
              >
                Forgot admin password?
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          textAlign="center"
          mt={2.5}
        >
          {lang.copyrightText || "© 2026 TorrentCloud. All rights reserved."}
        </Typography>
      </Container>
    </Box>
  );
};

export default AdminLogin;
